import { createContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types';
import { useScreenSize } from './custom-hooks/useScreenSize.jsx'
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAllUnitStatusOptions } from './graphqlCompnents/Inspection/fetchAllUnitStatusOptions.jsx'
import { fetchUnitDocumentTypes } from './graphqlCompnents/Unit/fetchUnitDocumentTypes.jsx';
import { fetchTeamMemberDataByLoginEmail } from './graphqlCompnents/TeamMember/fetchTeamMemberDataByLoginEmail.jsx'
import { fetchAccountsByTeamMember } from './graphqlCompnents/Statement/fetchAccountsByTeamMember.jsx';
import { fetchInstantNotificationsByLastFour } from './graphqlCompnents/Statement/fetchInstantNotificationsByLastFour.jsx';

const isMobile = () => {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
};

export const AppContext = createContext(null);

export const ContextWrapper = ({ children }) => {

  let isUserLoggedIn = false

  const { route } = useAuthenticator((context) => [context.route]);
  if (route === 'authenticated') {
    isUserLoggedIn = true
  }

  const [ userGroups, setUserGroups ] = useState([]);
  const [ userAttributes, setUserAttributes ] = useState([])
  const [ store, setStore ] = useState({
    userMessages: null,
    userData: null,
    inspectionStatuses: null,
    inspectionQueue: null,
    completedInspections: null,
    unitDocumentTypes: null,
    screenWidth: null,
    divisionHasOneClient: null,
    divisionAllowsEmailCC: null,
    enableAllSupervisors: null,
    defaultStartTime: null,
    defaultLunchDuration: null,
    defaultEndTime: null,
    showMaterialEquipmentMergeToggle: null,
    showDownloadPDF: null,
    usersAvatars: null,
    userIsMobile: null,
  });

  const [ userData, setUserData ] = useState(null)
  const [ divisionHasOneClient, setDivisionHasOneClient ] = useState(null)
  const [ divisionAllowsEmailCC, setDivisionAllowsEmailCC ] = useState(null)
  const [ enableAllSupervisors, setEnableAllSupervisors ] = useState(null)
  const [ defaultStartTime, setDefaultStartTime ] = useState(null)
  const [ defaultLunchDuration, setDefaultLunchDuration ] = useState(null)
  const [ defaultEndTime, setDefaultEndTime ] = useState(null)
  const [ showMergeMaterialEquipmentToggle, setShowMergeMaterialEquipmentToggle ] = useState(false)
  const [ showDownloadPDF, setShowDownloadPDF ] = useState(false)
  const [ teamMemberId, setTeamMemberId ] = useState(null)

  const qbEnvironment = import.meta.env.VITE_QBO_ENV || 'sandbox';

  const screenSize = useScreenSize();
  const [ screenWidth, setScreenWidth ] = useState(null)
  const [ inspectionStatuses, setInspectionStatuses ] = useState(null)
  const [ documentTypes, setDocumentTypes ] = useState(null)

  useEffect(() => {
    if (screenSize.width) {
      setScreenWidth(screenSize.width)
    } 
  },[screenSize])

  const actions = {
    clearStore: () => {
      setStore(null)
    }
  };

  useEffect(() => {
    async function fetchUserData() {
      try {
        const attributes = await fetchUserAttributes();
        setUserAttributes(attributes)
        const session = await fetchAuthSession();
        const groups = session.tokens.idToken?.payload['cognito:groups'] || [];
        setUserGroups(groups);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    isUserLoggedIn && fetchUserData();
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (userData && divisionAllowsEmailCC !== null) {
      setStore({ 
        userData: userData?.userData,
        userIsMobile: isMobile(),
        inspectionStatuses: inspectionStatuses,
        unitDocumentTypes: documentTypes,
        screenWidth: screenWidth,
        divisionHasOneClient: divisionHasOneClient,
        divisionAllowsEmailCC: divisionAllowsEmailCC,
        enableAllSupervisors: enableAllSupervisors,
        defaultStartTime: defaultStartTime,
        defaultLunchDuration: defaultLunchDuration,
        defaultEndTime: defaultEndTime,
        showMaterialEquipmentMergeToggle: showMergeMaterialEquipmentToggle,
        showDownloadPDF: showDownloadPDF,
        qbEnvironment: qbEnvironment,
      });
    }
  }, [
    userData, 
    inspectionStatuses, 
    documentTypes, 
    screenWidth, 
    userGroups,
    divisionHasOneClient, divisionAllowsEmailCC, enableAllSupervisors, 
    defaultStartTime, defaultLunchDuration, defaultEndTime, 
    showMergeMaterialEquipmentToggle, showDownloadPDF, qbEnvironment
  ]);

  const plaidEnv = 'production';
  const [ userAccounts, setUserAccounts ] = useState([])
  useEffect(() => {
    const fetchData = async () => {
      const accounts = await fetchAccountsByTeamMember(plaidEnv, teamMemberId);
      setUserAccounts(accounts);
    }
    if (teamMemberId) {
      fetchData()
    }
  },[teamMemberId, plaidEnv])

  const [ unsubmittedReceiptCount, setUnsubmittedReceiptCount ] = useState(null)
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!userAccounts || userAccounts.length === 0) return;
      try {
        const transactionPromises = userAccounts.map(async (account) => {
          const cardNumber = account.cardLastFour;
  
          const [trueTransactions, falseTransactions] = await Promise.allSettled([
            fetchInstantNotificationsByLastFour(cardNumber, true, 500),
            fetchInstantNotificationsByLastFour(cardNumber, false, 500),
          ]);
  
          const transactions = [];
          if (trueTransactions.status === 'fulfilled' && trueTransactions.value) {
            transactions.push(...trueTransactions.value);
          }
          if (falseTransactions.status === 'fulfilled' && falseTransactions.value) {
            transactions.push(...falseTransactions.value);
          }
  
          return transactions;
        });
  
        const allTransactionResults = await Promise.allSettled(transactionPromises);
  
        const successfulTransactions = allTransactionResults
          .filter(result => result.status === 'fulfilled' && result.value.length > 0)
          .map(result => result.value);
  
        const flattenedTransactions = successfulTransactions.flat();
  
        const [nullSubmitted, otherSubmitted] = flattenedTransactions.reduce(
          (acc, transaction) => {
            transaction.isSubmitted === null ? acc[0].push(transaction) : acc[1].push(transaction);
            return acc;
          },
          [[], []]
        );
  
        nullSubmitted.sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));
        otherSubmitted.sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));
  
        const sortedTransactions = [...nullSubmitted, ...otherSubmitted];
  
        let count = sortedTransactions.filter(item => item.isSubmitted !== true).length;
        setUnsubmittedReceiptCount(count||0);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      }
    };

    fetchTransactions();
  }, [userAccounts]);

  useEffect(() => {
    if (userAttributes.email) {
      getLoggedInUserData(userAttributes.email).then((results) => {
        console.log('setting user props');
        setTeamMemberId(results.id)
        setUserData({
          userData: {
            email: userAttributes.email, 
            name: results.firstName+' '+results.lastName, 
            divisionId: results.divisionId, 
            multiDivisions: results.multiDivisions,
            id: results.id, 
            signedUrl: null, 
            isGlobalAdmin: results.isGlobalAdmin || false,
            groups: userGroups,
            receiptCount: (unsubmittedReceiptCount) ? unsubmittedReceiptCount.toString() : '0',
          }
        })
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAttributes, userGroups, unsubmittedReceiptCount])

  const getLoggedInUserData = async (email) => {
    if (!email) return
    const userData = await fetchTeamMemberDataByLoginEmail(email)
    
    setDivisionHasOneClient(userData?.division?.djtOneClient||false)
    setDivisionAllowsEmailCC(userData?.division?.djtAllowsEmailCC||false)
    setEnableAllSupervisors(userData?.division?.djtEnableAllSupervisors||false)
    setDefaultStartTime(userData?.division?.djtDefaultStartTime||'08:00')
    setDefaultLunchDuration(userData?.division?.djtDefaultLunchDuration||'00:30')
    setDefaultEndTime(userData?.division?.djtDefaultEndTime||'17:00')
    setShowMergeMaterialEquipmentToggle(userData?.division?.djtMergeMaterialEquipment||false)
    setShowDownloadPDF(userData?.division?.djtShowDownloadPDF||false)

    return userData
  }

  useEffect(() => {
    const documentTypes = async () => {
      const unitDocumentTypes = await fetchUnitDocumentTypes()
      return unitDocumentTypes
    }
    documentTypes().then((data) => {
      setDocumentTypes(data)
    })
  },[])

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const unitStatuses = await fetchAllUnitStatusOptions();
        setInspectionStatuses(unitStatuses);
      } catch (error) {
        console.error('Error fetching unit statuses:', error);
      }
    };

    fetchStatuses();
  },[])

  return (
    <AppContext.Provider value={{ store, actions }}>
      {children}
    </AppContext.Provider>
  );
}

ContextWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};
